import { DatePipe } from "@angular/common";

declare var require: any;
let moment = require("moment");

export class DateHelper {
  static monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  static dateFormatter = {
    DDMMMYYYY: 'DD/MMM/YYYY',
    MMDDYYYY: 'MM/DD/YYYY',
    YYYYMMDD: 'YYYY-MM-DD'
  };
  static dateFormat = 'dd MMM yyyy HH:MM';
  /**
   * Parse date value from various formats to Date object
   * @param dateValue - The date value to parse (string, Date, or other format)
   * @returns Parsed Date object or null if invalid
   */
  static parseDateValue(dateValue: any): Date | null {
    if (!dateValue) {
      return null;
    }

    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    const parsedMoment = moment(dateValue, [this.dateFormatter.DDMMMYYYY, this.dateFormatter.MMDDYYYY, this.dateFormatter.YYYYMMDD, moment.ISO_8601], true);
    if (parsedMoment.isValid()) {
      return parsedMoment.toDate();
    }

    const parsedDate = new Date(dateValue);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /**
   * Convert a date value to date-only MM/DD/YYYY string without timezone shift.
   * Supports ISO dates and slash formats, including DD/MM/YYYY normalization.
   */
  static toDateOnlyDisplay(dateValue: any): string {
    if (!dateValue) {
      return '';
    }

    if (typeof dateValue === 'string') {
      const trimmedDate = dateValue.trim();

      const isoDateMatch = trimmedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDateMatch) {
        const [, year, month, day] = isoDateMatch;
        return `${month}/${day}/${year}`;
      }

      const slashDateMatch = trimmedDate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (slashDateMatch) {
        const first = Number(slashDateMatch[1]);
        const second = Number(slashDateMatch[2]);
        const year = slashDateMatch[3];

        if (first > 12 && second <= 12) {
          const day = String(first).padStart(2, '0');
          const month = String(second).padStart(2, '0');
          return `${month}/${day}/${year}`;
        }

        return `${slashDateMatch[1]}/${slashDateMatch[2]}/${year}`;
      }
    }

    if (dateValue instanceof Date) {
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');
      const year = dateValue.getFullYear();
      return `${month}/${day}/${year}`;
    }

    return '';
  }

  /**
   * Convert a date value to a local Date object using date-only parsing.
   * Uses toDateOnlyDisplay normalization (MM/DD/YYYY), then constructs Date(year, month-1, day).
   */
  static toDateOnlyDate(dateValue: any): Date | null {
    const formatted = DateHelper.toDateOnlyDisplay(dateValue);
    if (!formatted) {
      return null;
    }

    const match = formatted.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
      return null;
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    const localDate = new Date(year, month - 1, day);
    return isNaN(localDate.getTime()) ? null : localDate;
  }

  /**
   * Compare two dates
   * @param date1 - First date to compare
   * @param date2 - Second date to compare
   * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2, or null if either date is invalid
   */
  static dateCompare(date1: Date | null, date2: Date | null): number | null {
    if (!date1 || !date2 || isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      return null;
    }

    if (date1 < date2) {
      return -1;
    } else if (date1 > date2) {
      return 1;
    } else {
      return 0;
    }
  }

  static formatDateToDDMMMYYYY(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = this.monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day}${month}${year}`;
  } 
}

export class StringHelper {
  static middleInitialInvalid: boolean = false;
  static dobChecked: boolean = true;
  /**
   * Trim leading and trailing whitespace from a string
   * @param value - The string to trim
   * @returns Trimmed string or empty string if input is null/undefined
   */
  static trim(value: string | null | undefined): string {
    return (value || '').trim();
  }

  static charOnly(event) {
    return (event.charCode > 64 &&
      event.charCode < 91) || (event.charCode > 96 && event.charCode < 123)
  }

  static middleInitialOnly(event: KeyboardEvent) {
    const char = event.key;
    // Only allow A-Z, a-z, or hyphen
    if (!/^[a-zA-Z-]$/.test(char)) {
      event.preventDefault();
      StringHelper.middleInitialInvalid = true;
    } else {
      StringHelper.middleInitialInvalid = false;
    }
  }

  static changeToCSTFormat(date: Date | null): string {
    const datePipe = new DatePipe('en-US');
    return datePipe.transform(date, 'dd MMM yyyy HH:mm', 'America/Chicago') || '';
  }
}

export class ConversionHelper {
  static feetToInches(height: any): number {
    const feet = Math.floor(height);
    const inches = Math.round((height - feet) * 10); // e.g., 5.8 => 5 feet, 8 inches
    const totalInches = feet * 12 + inches;
    return totalInches;
  }

  static inchesToFeet(inches: number): number {
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return feet + remainingInches / 10;
  }
}
